import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CreateCountSheetDto, ReplaceCountSheetDto } from '@peritasai/types';
import type { CountSheetEntity } from './count-sheet.entity';
import { countSheetsRepository } from './repository';

export const countSheetsKeys = {
  all: ['count-sheets'] as const,
  lists: () => [...countSheetsKeys.all, 'list'] as const,
  pagedList: (page: number, pageSize: number, search: string) =>
    [...countSheetsKeys.all, 'paged', page, pageSize, search] as const,
  detail: (id: string) => [...countSheetsKeys.all, 'detail', id] as const,
} as const;

export function useCountSheets(): UseQueryResult<CountSheetEntity[]> {
  return useQuery({
    queryKey: countSheetsKeys.lists(),
    queryFn: () => countSheetsRepository.getCountSheets(),
    staleTime: 30 * 1000,
  });
}

export function useCreateCountSheet(): UseMutationResult<
  CountSheetEntity,
  Error,
  CreateCountSheetDto
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateCountSheetDto) => countSheetsRepository.createCountSheet(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: countSheetsKeys.lists() });
    },
  });
}

export function useReplaceCountSheet(): UseMutationResult<
  CountSheetEntity,
  Error,
  { id: string; dto: ReplaceCountSheetDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: ReplaceCountSheetDto }) =>
      countSheetsRepository.replaceCountSheet(id, dto),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: countSheetsKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: countSheetsKeys.detail(id) });
    },
  });
}

export interface CountSheetsPageParams {
  page: number;
  pageSize: number;
  search?: string;
}

export function usePagedCountSheets(
  params: CountSheetsPageParams
): UseQueryResult<{ data: CountSheetEntity[]; total: number; page: number; limit: number }> {
  const { page, pageSize, search = '' } = params;
  return useQuery({
    queryKey: countSheetsKeys.pagedList(page, pageSize, search),
    queryFn: () => countSheetsRepository.getPagedCountSheets({ page: page + 1, limit: pageSize, search }),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useCheckDuplicateCountSheet(trayId: string): UseQueryResult<CountSheetEntity | null> {
  return useQuery({
    queryKey: [...countSheetsKeys.all, 'search', trayId] as const,
    queryFn: () => countSheetsRepository.searchCountSheets(trayId.trim()),
    enabled: false,
    staleTime: 0,
    retry: false,
  });
}
